/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router';

import BookmarkIcon from '@mui/icons-material/Bookmark';
import { Box, Card, CardActions, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';

import { ClusterStates, ServiceType } from 'shared';

import { ServiceIcon } from '../../../components/ServiceIcon';
import { BasicRoutes } from '../../../constants/basicRoutes';
import { determineStatusColor } from '../../../utils/determineStatusColor';
import { prepareRoutePath } from '../../../utils/prepareRoutePath';
import { fetchClusterStatus } from '../../../utils/queryFns';

type Props = {
  toggleFavorite: (event: React.MouseEvent, serviceId: string) => void;
  organizationDisplayed: boolean;
  organization: {
    id: string;
    name: string;
  };
  project: {
    id: string;
    name: string;
  };
  id: string;
  name: string;
  type: ServiceType;
};

export const FavoriteServiceCard = memo((service: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { organization, project } = service;
  const { data: statusData, isLoading: statusLoading } = useQuery(
    [fetchClusterStatus.key, { organizationId: organization.id, projectId: project.id, clusterId: service.id }],
    fetchClusterStatus.fn,
    { enabled: !!service.id },
  );

  const onClick = useCallback(
    () =>
      navigate(
        prepareRoutePath(BasicRoutes.CLUSTER_PAGE, {
          ':organization_id': organization.id,
          ':project_id': project.id,
          ':cluster_id': service.id,
        }),
      ),
    [navigate, organization.id, project.id, service.id],
  );

  return (
    <Card
      data-cy={`home-page-favorite-cluster-${service.name}`}
      elevation={0}
      sx={{
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        margin: '5px 1px',
        '&:hover': {
          backgroundColor: '#F6F7F8',
        },
      }}
    >
      <CardActions>
        <Tooltip title={t('cluster:details.header.favorite.remove')}>
          <IconButton
            sx={{ padding: 0 }}
            onClick={e => service.toggleFavorite(e, service.id)}
            size="small"
            data-cy={`home-page-favorite-cluster-button-${service.name}`}
          >
            <BookmarkIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
      <Box
        position="relative"
        p={0.5}
        sx={{
          '&:hover': { cursor: 'pointer' },
        }}
        gap={0.5}
        onClick={onClick}
        display="flex"
        flexDirection="column"
      >
        <Stack gap={0.1}>
          <Typography variant="subtitle2" sx={{ fontSize: '13px', textTransform: 'capitalize' }}>
            {service.organizationDisplayed ? `${service.organization.name} /` : ''} {service.project.name}
          </Typography>
        </Stack>
        <Box display="flex" gap={1}>
          <ServiceIcon variant="minimal" id="icon" style={{ marginTop: 3 }} serviceType={service.type} />
          <h3 style={{ margin: 0, fontSize: '16px' }}>{service.name}</h3>
        </Box>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {statusLoading ? (
              <>
                <Skeleton variant="circular" width={8} height={8} />
                <Skeleton variant="text" width={80} height={15} />
              </>
            ) : (
              <>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: determineStatusColor(statusData?.payload?.status),
                  }}
                />
                <Typography variant="body2" color="textSecondary">
                  {statusData?.payload?.status || ClusterStates.Unavailable}
                </Typography>
              </>
            )}
          </Stack>
        </Box>
      </Box>
    </Card>
  );
});
